import type { IFantasySelectionContest, ILeague as IFantasySelectionLeague, IPool, ITeam } from '@/interface/IFantasySelection';
import type {
    IBet, IContest, IFantasySelectionMyBetDetail, IFantasySelectionMyBets, ILeague, IMatch, IRanking, ISelection,
} from '@/interface/IFantasySelectionMyBets';
import { ContestStatus, FantasySelectionBetStatus, SportType } from '@/interface/enum';
import type {
    BetContestResponse, BetLeagueResponse, ContestLeagueResponse, ContestResponse, FantasySelectionBetResponse, FantasySelectionMatchResponse, FantasySelectionMyBetDetailResponse, FantasySelectionMyBetsResponse, FantasySelectionResponse,
    FantasySelectionTeamResponse,
    PoolResponse, RankingResponse,
} from '@/interface/pollux';

export function formatMyBets(myBets: FantasySelectionMyBetsResponse): IFantasySelectionMyBets {
    return {
        running: myBets.runningBets.map(formatBet),
        settled: myBets.settledBets.map(formatBet),
    };
}

export function formatMyBetDetail(myBetDetail: FantasySelectionMyBetDetailResponse): IFantasySelectionMyBetDetail {
    return {
        contest: formatContest(myBetDetail.contest),
        rankings: myBetDetail.rankings.map(formatRanking),
        stake: myBetDetail.stake,
        betStatus: FantasySelectionBetStatus.fromPollux(myBetDetail.betStatus),
        prizePool: myBetDetail.prizePool,
    };
}

function formatContest(contest: ContestResponse): IFantasySelectionContest {
    return {
        id: contest.id,
        names: contest.name,
        startTime: new Date(contest.startTime),
        endTime: new Date(contest.endTime),
        pools: [formatPool(contest.pool)],
        leagues: contest.leagues.map(formatContestLeague),
        matches: contest.matches.map(formatMatch),
    };
}

function formatContestLeague(league: ContestLeagueResponse): IFantasySelectionLeague {
    return {
        id: league.id,
        names: league.name,
    };
}

function formatMatch(match: FantasySelectionMatchResponse): IMatch {
    return {
        matchId: match.matchId,
        leagueId: match.leagueId,
        homeTeam: formatTeam(match.homeTeam),
        awayTeam: formatTeam(match.awayTeam),
        kickoffTime: new Date(match.kickoffTime),
        homeScore: match.homeScore ?? null,
        awayScore: match.awayScore ?? null,
    };
}

export function formatBet(bet: FantasySelectionBetResponse): IBet {
    return {
        refNo: bet.refNo,
        contest: formatMyBetContest(bet.contest),
        pool: formatPool(bet.pool),
        stake: bet.stake,
        betTime: new Date(bet.betTime),
        status: FantasySelectionBetStatus.fromPollux(bet.status),
        winLostDate: bet.winLostDate ? new Date(bet.winLostDate) : undefined,
        winLost: bet.winLost ?? 0,
        sportType: SportType.fromPollux(bet.sportType),
        playerCommission: bet.playerCommission,
        leagues: bet.leagues.map(formatLeague),
        selections: bet.selections.map(formatSelection),
        myRank: bet.myRank,
    };
}

function formatMyBetContest(contest: BetContestResponse): IContest {
    return {
        id: contest.id,
        name: contest.name,
        status: ContestStatus.fromPollux(contest.status),
    };
}

function formatPool(pool: PoolResponse): IPool {
    return {
        id: pool.id,
        entryFee: pool.entryFee,
        prizePool: pool.prizePool,
        currentEntries: pool.currentEntries,
        maxEntries: pool.maxEntries,
    };
}

function formatLeague(league: BetLeagueResponse): ILeague {
    return {
        id: league.id,
        name: league.name,
    };
}

function formatRanking(ranking: RankingResponse): IRanking {
    return {
        rank: ranking.rank,
        playerName: ranking.playerName,
        points: ranking.points,
        selections: ranking.selections.map(formatSelection),
    };
}

function formatSelection(selection: FantasySelectionResponse): ISelection {
    return {
        id: selection.id,
        name: selection.name,
    };
}

function formatTeam(team: FantasySelectionTeamResponse): ITeam {
    return {
        id: team.id,
        name: team.name,
    };
}
